/**
 * Lógica pura del entrenamiento (Req. 11, 12; design.md, 8.2 y 8.3).
 *
 * - "La vez pasada": las series hechas de la última sesión TERMINADA que
 *   tuvo el ejercicio, en la unidad con que se capturaron (Req. 11.3).
 * - Prellenado: las series objetivo con la carga y las repeticiones de la
 *   vez pasada; sin historial, vacías (Req. 11.2). No se precarga nada más.
 * - Biseries: los ejercicios con la misma clave se alternan; el descanso
 *   empieza al completar la serie de todos (decisión de Janeth, 23 oct 2026).
 * - El descanso se guarda como su hora de fin (Req. 12.5): el tiempo que falta
 *   se calcula, no se cuenta, así que sigue correcto si iOS suspende la app.
 */

import type { RoutineExercise, Workout, WorkoutExercise, WorkoutSet, TipoSerie } from '@/db/types';
import type { FechaLocal } from './dates';
import {
  aKg,
  desdeKg,
  formatearCarga,
  type CargaCapturada,
  type UnidadCarga,
  type UnidadPeso,
} from './units';
import { formatearNumero, redondear } from './numbers';

// ---------------------------------------------------------------------------
// La vez pasada y prellenado
// ---------------------------------------------------------------------------

export interface SesionAnterior {
  workoutId: string;
  fecha: FechaLocal;
  series: WorkoutSet[];
  /** La pausa y la variante con que se hizo esa vez (0.3.4), si se anotaron. */
  pausaSeg?: number | null;
  variante?: string | null;
}

/**
 * La última sesión terminada con este ejercicio, con sus series hechas en
 * orden. `entrenamientos` son los dueños de esas series.
 */
export function ultimaSesion(
  series: readonly WorkoutSet[],
  entrenamientos: ReadonlyMap<string, Pick<Workout, 'id' | 'fecha' | 'inicio' | 'estado'>>,
  excluirWorkoutId: string | null = null,
): SesionAnterior | null {
  let mejor: Pick<Workout, 'id' | 'fecha' | 'inicio'> | null = null;
  for (const s of series) {
    if (!s.hecha || s.workoutId === excluirWorkoutId) continue;
    const w = entrenamientos.get(s.workoutId);
    if (w === undefined || w.estado !== 'terminado') continue;
    if (mejor === null || w.inicio > mejor.inicio) mejor = w;
  }
  if (mejor === null) return null;
  const elegido = mejor;
  return {
    workoutId: elegido.id,
    fecha: elegido.fecha,
    series: series.filter((s) => s.hecha && s.workoutId === elegido.id).sort((a, b) => a.orden - b.orden),
  };
}

/** La carga anterior en la unidad de hoy: kg y lb se convierten; las placas no. */
export function cargaEnUnidad(carga: CargaCapturada | null, unidad: UnidadCarga): CargaCapturada | null {
  if (carga === null) return null;
  if (carga.unidad === unidad) return carga;
  if (carga.unidad === 'placa' || unidad === 'placa') return null;
  return { valor: redondear(desdeKg(aKg(carga.valor, carga.unidad), unidad), 2), unidad };
}

export interface SerieBorrador {
  tipoSerie: TipoSerie;
  pesoCapturado: CargaCapturada | null;
  reps: number | null;
  /** Solo en los ejercicios por lado (0.3.5). */
  repsIzq?: number | null;
  repsDer?: number | null;
  duracionSeg: number | null;
  distanciaM: number | null;
}

/** ¿La serie se anotó por lado? */
export function anotadaPorLado(s: Partial<Pick<WorkoutSet, 'repsIzq' | 'repsDer'>>): boolean {
  return (s.repsIzq ?? null) !== null || (s.repsDer ?? null) !== null;
}

/** Cómo se hace un ejercicio en una sesión: lo que se copia de su rutina. */
export type ConfiguracionEnSesion = Pick<
  WorkoutExercise,
  'repsMin' | 'repsMax' | 'descansoSeg' | 'notas' | 'biserie' | 'descansoBiserieSeg'
> & { porLado?: true };

/**
 * La configuración de un ejercicio de la rutina para la sesión (0.3.8): el
 * objetivo, el descanso, las indicaciones, la biserie y si se anota por lado.
 * Es la misma al empezar el entrenamiento y al volver a agregar el ejercicio
 * (incidencia del 26 oct 2026: el desplante volvía sin la captura por lado).
 * La pausa y la variante de casa salen aparte, de la vez pasada o de la rutina.
 */
export function configuracionDeRutina(
  item: Pick<RoutineExercise, 'repsMin' | 'repsMax' | 'descansoSeg' | 'notas' | 'biserie' | 'descansoBiserieSeg' | 'porLado'>,
): ConfiguracionEnSesion {
  return {
    repsMin: item.repsMin,
    repsMax: item.repsMax,
    descansoSeg: item.descansoSeg,
    notas: item.notas,
    biserie: item.biserie ?? null,
    descansoBiserieSeg: item.descansoBiserieSeg ?? null,
    ...(item.porLado === true ? { porLado: true as const } : {}),
  };
}

/** Los ejercicios que se pueden anotar por lado: los de repeticiones, con o sin carga. */
export function admitePorLado(tipo: string): boolean {
  return tipo === 'reps' || tipo === 'peso_reps';
}

/** Un ejercicio en el entrenamiento, con lo que hace falta para saber dónde va. */
export type EnSesion = Pick<WorkoutExercise, 'id' | 'exerciseId' | 'orden' | 'biserie'>;

/** Dónde queda un ejercicio que vuelve o entra, y con qué biserie. */
export interface Lugar {
  /** Índice entre los que ya están, en orden: va antes del que ocupa ese lugar. */
  posicion: number;
  biserie: string | null;
}

/**
 * La biserie con que entra un ejercicio que se agrega (0.3.9): la suya solo
 * si su pareja está en el entrenamiento y él no está ya en esa biserie (si
 * no, la copia formaría una biserie de tres). Si no, va solo.
 */
export function biserieAlVolver(items: readonly Pick<WorkoutExercise, 'exerciseId' | 'biserie'>[], exerciseId: string, clave: string | null | undefined): string | null {
  const k = clave ?? null;
  if (k === null) return null;
  const miembros = items.filter((x) => (x.biserie ?? null) === k);
  return miembros.length > 0 && !miembros.some((m) => m.exerciseId === exerciseId) ? k : null;
}

/** El índice más cercano a `indice`, hacia adelante, que no queda dentro de un bloque de biserie. */
function sinPartir(ordenados: readonly EnSesion[], indice: number): number {
  let i = Math.max(0, Math.min(ordenados.length, indice));
  while (i > 0 && i < ordenados.length) {
    const antes = ordenados[i - 1]?.biserie ?? null;
    if (antes !== null && antes === (ordenados[i]?.biserie ?? null)) i += 1;
    else break;
  }
  return i;
}

/** El último índice de esa biserie entre los ordenados; -1 si no está. */
function ultimoDeBiserie(ordenados: readonly EnSesion[], clave: string): number {
  return ordenados.reduce((max, x, i) => ((x.biserie ?? null) === clave ? i : max), -1);
}

/** Los vecinos de un ejercicio en su entrenamiento: para devolverlo después a su lugar. */
export interface VecinosEnSesion {
  /** El que iba antes; null si era el primero. */
  despuesDe: string | null;
  /** El que iba después; null si era el último. */
  antesDe: string | null;
}

export function vecinosDe(items: readonly EnSesion[], id: string): VecinosEnSesion {
  const ordenados = [...items].sort(porOrden);
  const i = ordenados.findIndex((x) => x.id === id);
  if (i === -1) return { despuesDe: null, antesDe: null };
  return { despuesDe: ordenados[i - 1]?.id ?? null, antesDe: ordenados[i + 1]?.id ?? null };
}

/**
 * Dónde vuelve un ejercicio quitado al deshacerlo (0.3.9), entre los que
 * quedan. Lo usan la base y el aviso de "Deshacer", para que el aviso esté
 * donde va a volver.
 *
 * - Si su pareja de biserie sigue aquí, vuelve a la biserie en el lugar que
 *   tenía dentro de ella (antes o después de su pareja).
 * - Si no, va antes del que iba después de él o, si ya no está, después del
 *   que iba antes, aunque se haya cambiado el orden; si no queda ninguno de
 *   los dos, por su número de orden. Nunca dentro de una biserie ajena (antes
 *   la partía al cambiar el orden).
 * - Va solo con su biserie si nadie más la tiene: su pareja puede volver a
 *   ella al agregarla. Si ya hay otro igual en esa biserie, va sin biserie.
 */
export function lugarAlRestaurar(
  items: readonly EnSesion[],
  quitado: Pick<WorkoutExercise, 'exerciseId' | 'orden' | 'biserie'>,
  vecinos: VecinosEnSesion | null,
): Lugar {
  const ordenados = [...items].sort(porOrden);
  const indice = (id: string | null | undefined) => (id == null ? -1 : ordenados.findIndex((x) => x.id === id));
  const antes = indice(vecinos?.antesDe);
  const despues = indice(vecinos?.despuesDe);
  const clave = quitado.biserie ?? null;
  const conPareja = biserieAlVolver(ordenados, quitado.exerciseId, clave);
  if (conPareja !== null) {
    const esDeLaBiserie = (i: number) => i !== -1 && (ordenados[i]?.biserie ?? null) === conPareja;
    const posicion = esDeLaBiserie(antes) ? antes : esDeLaBiserie(despues) ? despues + 1 : ultimoDeBiserie(ordenados, conPareja) + 1;
    return { posicion, biserie: conPareja };
  }
  const deseado = antes !== -1 ? antes : despues !== -1 ? despues + 1 : ordenados.filter((x) => x.orden < quitado.orden).length;
  const nadieLaTiene = clave !== null && !ordenados.some((x) => (x.biserie ?? null) === clave);
  return { posicion: sinPartir(ordenados, deseado), biserie: nadieLaTiene ? clave : null };
}

/**
 * Dónde entra un ejercicio que se agrega (0.3.9): al final o, si su pareja de
 * biserie está aquí (y él no está ya en esa biserie), justo después de ella.
 */
export function lugarAlAgregar(items: readonly EnSesion[], exerciseId: string, clave: string | null | undefined): Lugar {
  const ordenados = [...items].sort(porOrden);
  const biserie = biserieAlVolver(ordenados, exerciseId, clave);
  if (biserie === null) return { posicion: ordenados.length, biserie: null };
  return { posicion: ultimoDeBiserie(ordenados, biserie) + 1, biserie };
}

/**
 * Las series de hoy: tantas como el objetivo (o como la vez pasada si no hay
 * objetivo), con los valores de la vez pasada serie por serie. Las de
 * calentamiento de la vez pasada no se usan para prellenar las de trabajo.
 * Por lado (0.3.5), las repeticiones de cada lado de la vez pasada; si esa
 * vez no fue por lado, vacías: el total no se reparte entre los dos.
 */
export function prellenarSeries(
  objetivoSeries: number | null,
  anterior: SesionAnterior | null,
  unidad: UnidadCarga,
  porLado = false,
): SerieBorrador[] {
  const trabajo = (anterior?.series ?? []).filter((s) => s.tipoSerie !== 'calentamiento');
  const cuantas = objetivoSeries ?? Math.max(1, trabajo.length);
  const borradores: SerieBorrador[] = [];
  for (let i = 0; i < cuantas; i++) {
    // Si hoy hay más series que la vez pasada, la última sirve de referencia
    const previa = trabajo[i] ?? trabajo[trabajo.length - 1] ?? null;
    borradores.push({
      tipoSerie: 'normal',
      pesoCapturado: previa === null ? null : cargaEnUnidad(previa.pesoCapturado, unidad),
      ...(porLado
        ? { reps: null, repsIzq: previa?.repsIzq ?? null, repsDer: previa?.repsDer ?? null }
        : { reps: previa?.reps ?? null }),
      duracionSeg: previa?.duracionSeg ?? null,
      distanciaM: previa?.distanciaM ?? null,
    });
  }
  return borradores;
}

// ---------------------------------------------------------------------------
// Descanso al marcar una serie (Req. 12.1) y biseries
// ---------------------------------------------------------------------------

export type DespuesDeMarcar =
  | { tipo: 'descanso'; segundos: number }
  /** Biserie: falta la serie de otro ejercicio de la ronda; sin descanso todavía. */
  | { tipo: 'sigue'; workoutExerciseId: string }
  | { tipo: 'ninguno' };

const porOrden = <T extends { orden: number }>(a: T, b: T) => a.orden - b.orden;

/**
 * En el orden en que están, los ejercicios seguidos de la misma biserie
 * forman un bloque; los demás van solos. Así se muestran juntos y se mueven
 * juntos al reordenar (Req. 10.6, 11.6).
 */
export function agruparPorBiserie<T>(items: readonly T[], biserieDe: (x: T) => string | null | undefined): T[][] {
  const bloques: T[][] = [];
  for (const x of items) {
    const anterior = bloques[bloques.length - 1];
    const clave = biserieDe(x) ?? null;
    const primero = anterior?.[0];
    if (anterior !== undefined && primero !== undefined && clave !== null && (biserieDe(primero) ?? null) === clave) {
      anterior.push(x);
    } else {
      bloques.push([x]);
    }
  }
  return bloques;
}

/** Los ejercicios de la biserie de `we` en este entrenamiento, en orden; solo él si va solo. */
export function miembrosDeBiserie(we: WorkoutExercise, ejercicios: readonly WorkoutExercise[]): WorkoutExercise[] {
  const clave = we.biserie ?? null;
  if (clave === null) return [we];
  return ejercicios.filter((x) => (x.biserie ?? null) === clave).sort(porOrden);
}

/**
 * Qué sigue después de marcar `serieId` como hecha. `series` ya incluye la
 * serie marcada como hecha.
 */
export function despuesDeMarcar(
  serieId: string,
  ejercicios: readonly WorkoutExercise[],
  series: readonly WorkoutSet[],
): DespuesDeMarcar {
  const serie = series.find((s) => s.id === serieId);
  if (serie === undefined) return { tipo: 'ninguno' };
  const we = ejercicios.find((x) => x.id === serie.workoutExerciseId);
  if (we === undefined) return { tipo: 'ninguno' };
  const miembros = miembrosDeBiserie(we, ejercicios);
  if (miembros.length <= 1) return { tipo: 'descanso', segundos: we.descansoSeg };

  const deEjercicio = (id: string) => series.filter((s) => s.workoutExerciseId === id).sort(porOrden);
  const ronda = deEjercicio(we.id).findIndex((s) => s.id === serieId);
  const completa = miembros.every((m) => deEjercicio(m.id)[ronda]?.hecha !== false);
  if (completa) {
    const segundos = Math.max(...miembros.map((m) => m.descansoBiserieSeg ?? m.descansoSeg));
    return { tipo: 'descanso', segundos };
  }
  // El siguiente de la biserie con la serie de esta ronda pendiente
  const i = miembros.findIndex((m) => m.id === we.id);
  for (let paso = 1; paso < miembros.length; paso++) {
    const otro = miembros[(i + paso) % miembros.length];
    if (otro !== undefined && deEjercicio(otro.id)[ronda]?.hecha === false) {
      return { tipo: 'sigue', workoutExerciseId: otro.id };
    }
  }
  return { tipo: 'ninguno' };
}

// ---------------------------------------------------------------------------
// Temporizador (Req. 12.2, 12.5)
// ---------------------------------------------------------------------------

export interface Descanso {
  terminaEn: string;
  duracionSeg: number;
}

export function iniciarDescanso(segundos: number, ahora: Date): Descanso {
  return { terminaEn: new Date(ahora.getTime() + segundos * 1000).toISOString(), duracionSeg: segundos };
}

/** Segundos que faltan; negativo si ya terminó. */
export function segundosRestantes(descanso: Descanso, ahora: Date): number {
  return (Date.parse(descanso.terminaEn) - ahora.getTime()) / 1000;
}

/** ±15 s (Req. 12.2). Nunca deja un fin anterior a ahora. */
export function ajustarDescanso(descanso: Descanso, deltaSeg: number, ahora: Date): Descanso {
  const fin = Math.max(ahora.getTime(), Date.parse(descanso.terminaEn) + deltaSeg * 1000);
  return { terminaEn: new Date(fin).toISOString(), duracionSeg: Math.max(0, descanso.duracionSeg + deltaSeg) };
}

/** "1:30", "0:05", "12:04". */
export function formatearReloj(segundos: number): string {
  const s = Math.max(0, Math.ceil(segundos));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** "2 min", "75 s", "1 min 30 s". */
export function textoDescanso(segundos: number): string {
  if (segundos < 60) return `${segundos} s`;
  const min = Math.floor(segundos / 60);
  const resto = segundos % 60;
  return resto === 0 ? `${min} min` : `${min} min ${resto} s`;
}

// ---------------------------------------------------------------------------
// Presentación y resumen (Req. 11.10)
// ---------------------------------------------------------------------------

/** "3 × 8–10", "2 × 12", "3 × 10–15 por lado", "3 series". */
export function textoObjetivo(series: number, repsMin: number | null, repsMax: number | null, porLado = false): string {
  if (repsMin === null && repsMax === null) return `${series} ${series === 1 ? 'serie' : 'series'}`;
  const reps = repsMin !== null && repsMax !== null && repsMin !== repsMax ? `${repsMin}–${repsMax}` : String(repsMin ?? repsMax);
  return `${series} × ${reps}${porLado ? ' por lado' : ''}`;
}

type SerieParaTexto = Pick<WorkoutSet, 'pesoCapturado' | 'reps' | 'duracionSeg' | 'distanciaM'> &
  Partial<Pick<WorkoutSet, 'repsIzq' | 'repsDer'>>;

/** "40 lb × 10", "7 placas × 12", "× 15", "12 izq. · 11 der.", "1:30", "2 km en 12:00". */
export function textoSerie(s: SerieParaTexto): string {
  if (s.distanciaM !== null) {
    const km = formatearNumero(s.distanciaM / 1000, { decimales: 2, fijos: false });
    return s.duracionSeg === null ? `${km} km` : `${km} km en ${formatearReloj(s.duracionSeg)}`;
  }
  const carga = s.pesoCapturado === null ? null : formatearCarga(s.pesoCapturado);
  if (anotadaPorLado(s)) {
    const lados = `${s.repsIzq ?? '—'} izq. · ${s.repsDer ?? '—'} der.`;
    return carga === null ? lados : `${carga} × ${lados}`;
  }
  if (s.duracionSeg !== null && s.reps === null) return formatearReloj(s.duracionSeg);
  const reps = s.reps === null ? null : `× ${s.reps}`;
  return [carga, reps].filter((x) => x !== null).join(' ') || '—';
}

/** Las repeticiones de una serie; por lado, las de los dos lados. */
export function repsDeSerie(s: Pick<WorkoutSet, 'reps'> & Partial<Pick<WorkoutSet, 'repsIzq' | 'repsDer'>>): number {
  return (s.reps ?? 0) + (s.repsIzq ?? 0) + (s.repsDer ?? 0);
}

export interface ResumenEntrenamiento {
  duracionSeg: number;
  /** Series de trabajo hechas (sin calentamiento). */
  seriesHechas: number;
  calentamientos: number;
  /** kg × repeticiones de las series de trabajo con carga en kg o lb (por lado, las de los dos). */
  volumenKg: number;
  /** Series hechas con carga en placas: no entran al volumen. */
  seriesEnPlacas: number;
  ejercicios: number;
  /**
   * Repeticiones de las series de trabajo, con carga o sin ella (lo que
   * cuenta en casa). Por lado, las de los dos lados.
   */
  repeticiones: number;
}

export function resumenEntrenamiento(
  workout: Pick<Workout, 'inicio' | 'fin'>,
  series: readonly WorkoutSet[],
  ahora: Date = new Date(),
): ResumenEntrenamiento {
  const fin = workout.fin === null ? ahora.getTime() : Date.parse(workout.fin);
  const hechas = series.filter((s) => s.hecha);
  const trabajo = hechas.filter((s) => s.tipoSerie !== 'calentamiento');
  return {
    duracionSeg: Math.max(0, Math.round((fin - Date.parse(workout.inicio)) / 1000)),
    seriesHechas: trabajo.length,
    calentamientos: hechas.length - trabajo.length,
    volumenKg: trabajo.reduce((suma, s) => suma + (s.pesoKg ?? 0) * repsDeSerie(s), 0),
    seriesEnPlacas: trabajo.filter((s) => s.pesoCapturado?.unidad === 'placa').length,
    ejercicios: new Set(hechas.map((s) => s.workoutExerciseId)).size,
    repeticiones: trabajo.reduce((suma, s) => suma + repsDeSerie(s), 0),
  };
}

/** "45 min", "1 h 05 min". */
export function textoDuracion(segundos: number): string {
  const min = Math.round(segundos / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min`;
}

/** "2,450 lb" en la unidad preferida, sin decimales. */
export function textoVolumen(kg: number, unidad: UnidadPeso): string {
  return `${formatearNumero(Math.round(desdeKg(kg, unidad)), { decimales: 0 })} ${unidad}`;
}

/** Qué se registra en cada equipo (reglas de carga de la rutina de Janeth). */
export function ayudaDeCarga(equipo: string, unidad: UnidadCarga): string | null {
  if (unidad === 'placa') return 'El número de placa que marca la máquina.';
  if (equipo === 'mancuernas') return 'Peso de cada mancuerna.';
  if (equipo === 'barra') return 'Peso total, con la barra.';
  if (equipo === 'maquina' || equipo === 'polea') return 'Como lo marca el aparato.';
  return null;
}
